import { useRef } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { JSX } from "react";
import { FadeUp, MotionButton } from "@/lib/animations/components";
import {
  staggerOrchestrator,
  lineReveal,
  scaleXRevealProps,
  arrowNudge,
} from "@/lib/animations/variants";

const CONTAINER = "max-w-7xl mx-auto px-6";

export function HeroSection(): JSX.Element {
  const heroRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const parallaxY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);

  return (
    <section className="bg-cream">
      <div className="lg:hidden">
        <FadeUp>
          <img
            src="/images/Rosewood 1.png"
            alt="Rosewood Tax Consultancy"
            className="w-full h-auto block"
          />
        </FadeUp>
        <div className={`${CONTAINER} py-12`}>
          <HeroContent />
        </div>
      </div>

      <div ref={heroRef} className="hidden lg:flex min-h-screen relative">
        <div
          className="flex-1 flex items-center py-24"
          style={{ paddingLeft: "max(1.5rem, calc((100vw - 1280px) / 2))" }}
        >
          <div className="max-w-lg">
            <HeroContent />
          </div>
        </div>

        <motion.div
          className="w-[50vw] flex-shrink-0 relative"
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
        >
          <div
            className="sticky top-0 h-screen flex items-center justify-center overflow-hidden"
            style={{
              clipPath: "polygon(10% 0, 100% 0, 100% 100%, 0% 100%, 0% 8%)",
            }}
          >
            <motion.img
              src="/images/Rosewood 1.png"
              alt="Rosewood Tax Consultancy professionals"
              className="w-full h-full object-contain"
              style={{
                scale: 1.4,
                y: parallaxY,
                willChange: "transform",
              }}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function HeroContent() {
  return (
    <>
      <motion.h1
        variants={staggerOrchestrator(0.13, 0.15)}
        initial="initial"
        animate="animate"
        className="text-5xl xl:text-[62px] font-bold leading-[1.06] tracking-tight mb-6"
      >
        <motion.span variants={lineReveal} className="block">
          Expert Tax
        </motion.span>

        <motion.span variants={lineReveal} className="block">
          Advisory &
        </motion.span>

        <motion.span variants={lineReveal} className="block">
          Business Consulting.
        </motion.span>
      </motion.h1>

      <motion.div
        {...scaleXRevealProps(0.54)}
        className="origin-left w-14 h-[3px] bg-gold mb-6"
      />

      <FadeUp
        delay={0.62}
        className="text-lg text-neutral-700 leading-relaxed mb-8"
      >
        Helping businesses navigate tax compliance, reduce risk, and achieve
        sustainable growth through expert advisory, KRA compliance, eTIMS
        solutions, and strategic business consulting.
      </FadeUp>

      <FadeUp delay={0.84} className="flex flex-wrap items-center gap-4">
        <CTAButtons />
      </FadeUp>
    </>
  );
}

function CTAButtons() {
  return (
    <>
      <MotionButton lift>
        <Link
          to="/contact"
          className="group inline-flex items-center gap-2 px-7 py-3.5 bg-burgundy text-white font-semibold rounded-xl hover:bg-[#4A1219] transition-colors"
        >
          Book Free Consultation
          <motion.span
            variants={arrowNudge}
            initial="rest"
            whileHover="hovered"
            className="inline-flex"
          >
            <ArrowRight size={16} />
          </motion.span>
        </Link>
      </MotionButton>
    </>
  );
}
